import { forwardRef } from 'react';
import { Checkbox } from 'antd';
import clsx from 'clsx';
import styles from './MultiOption.module.css';

/**
 * @typedef {Object} MultiOptionOwnProps
 * @property {import('react').ReactNode} label - Leading badge content (e.g. a letter).
 * @property {'correct' | 'incorrect'} [state] - Overrides the row/badge background to the
 *   success/error feedback tokens, independent of `checked`.
 * @property {import('react').ReactNode} children - Main option content.
 */

/** @typedef {MultiOptionOwnProps & Omit<import('antd').CheckboxProps, 'children'>} MultiOptionProps */

/**
 * The multiple-selection counterpart to `Option` — a thin wrapper around antd's own `Checkbox`
 * so it keeps native `<input type="checkbox">` semantics and `Checkbox.Group` integration. The
 * badge is a rounded square and a selected option shows a check pip on its top-right corner.
 * Pair with `Option.Multi.Group`.
 *
 * @param {MultiOptionProps} props
 */
const MultiOption = forwardRef(({ label, state, className, children, ...rest }, ref) => (
    <Checkbox
        ref={ref}
        className={clsx(
            styles.vecMultiOption,
            state === 'correct' && styles.vecMultiOptionCorrect,
            state === 'incorrect' && styles.vecMultiOptionIncorrect,
            className,
        )}
        {...rest}
    >
        <span className={styles.vecMultiOptionLead}>
            <span className={styles.vecMultiOptionBadge}>
                {label}
                <svg
                    className={styles.vecMultiOptionPip}
                    viewBox="0 0 16 16"
                    width="16"
                    height="16"
                    aria-hidden="true"
                    focusable="false"
                >
                    <path
                        d="M4.5 8.5l2.5 2.5 4.5-5"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </span>
        </span>
        <span className={styles.vecMultiOptionContent} data-vec-option-content>
            {children}
        </span>
    </Checkbox>
));
MultiOption.displayName = 'MultiOption';

export default MultiOption;
